import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/cart";
import { Minus, Plus, Trash2, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

export default function CartSheet() {
  const { items, update, remove, totals, open, setOpen, clear, revalidate } = useCart();
  const nav = useNavigate();

  // Revalidate stock every time the cart sheet opens
  useEffect(() => { if (open) { revalidate(); } /* eslint-disable-next-line */ }, [open]);

  const goToCheckout = async () => {
    const res = await revalidate();
    if (!res.all_ok) return; // toasts fired inside revalidate; user stays in cart to review
    setOpen(false);
    nav("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#F9F6F0] border-l border-[#E5E1D8] p-0 flex flex-col"
        data-testid="cart-sheet"
      >
        <SheetHeader className="p-6 border-b border-[#E5E1D8]">
          <SheetTitle className="font-serif text-3xl text-[#1A1714]">Your Basket</SheetTitle>
          <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            {totals.count} {totals.count === 1 ? "volume" : "volumes"}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="cart-items">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-serif text-2xl italic text-[#1A1714]/70">A quiet basket.</p>
              <p className="font-mono text-xs text-[#1A1714]/50 mt-2">Add a book to begin.</p>
            </div>
          ) : (
            items.map((i) => {
              const stock = Number.isFinite(i.stock) ? i.stock : 99;
              const atMax = i.quantity >= stock;
              return (
                <div key={`${i.book_id}-${i.format}`} className="flex gap-4" data-testid={`cart-item-${i.book_id}`}>
                  <div className="book-frame w-20 h-28 flex-shrink-0">
                    <img src={i.cover_image} alt={i.title} />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-lg leading-tight text-[#1A1714]">{i.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mt-1">
                      {i.author} · {i.format}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2 border border-[#E5E1D8]">
                        <button
                          onClick={() => update(i.book_id, i.format, i.quantity - 1)}
                          className="p-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                          disabled={i.quantity <= 1}
                          data-testid={`cart-dec-${i.book_id}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="font-mono text-xs w-6 text-center">{i.quantity}</span>
                        <button
                          onClick={() => update(i.book_id, i.format, i.quantity + 1)}
                          className="p-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                          disabled={atMax}
                          data-testid={`cart-inc-${i.book_id}`}
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <span className="font-mono text-sm">${(i.price * i.quantity).toFixed(2)}</span>
                      <button onClick={() => remove(i.book_id, i.format)} data-testid={`cart-remove-${i.book_id}`} aria-label="Remove">
                        <Trash2 size={14} className="text-[#1A1714]/50 hover:text-[#7A2021]" />
                      </button>
                    </div>
                    {atMax && (
                      <p className="mt-1 text-[10px] font-medium text-amber-700 flex items-center gap-1" data-testid={`cart-at-max-${i.book_id}`}>
                        <AlertTriangle size={11} /> Max available: {stock}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {items.length > 0 && (
          <div className="p-6 border-t border-[#E5E1D8] space-y-4 bg-[#F9F6F0]">
            <div className="flex justify-between font-mono text-sm">
              <span className="uppercase tracking-editorial text-[10px] text-[#1A1714]/60">Subtotal</span>
              <span data-testid="cart-subtotal">${totals.subtotal.toFixed(2)}</span>
            </div>
            <Button
              data-testid="cart-checkout-btn"
              onClick={goToCheckout}
              className="w-full rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-12"
            >
              Continue to checkout →
            </Button>
            <button onClick={clear} className="w-full font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 hover:text-[#7A2021]">
              Clear basket
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
